import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { OfflineBanner } from '../../components/lesson/OfflineBanner.js';
import { PhotoCapture } from '../../components/lesson/PhotoCapture.js';
import { RephrasePanel } from '../../components/lesson/RephrasePanel.js';
import { PromptDraftPanel } from '../../components/lesson/PromptDraftPanel.js';
import { ApiError } from '../../lib/api.js';
import {
  createLesson,
  disposePromptDraft,
  fetchLesson,
  fetchReference,
  submitLesson,
  updateLesson,
  type IssueCategory,
  type IssueImpact,
  type Lesson,
  type LessonPhoto,
  type RephrasableField,
} from '../../lib/lesson-api.js';
import { captureGps, describeGps, type GpsFix } from '../../lib/offline/capture.js';
import { listQueuedPhotos, queueCreate, queueFieldChange, readCachedLesson } from '../../lib/offline/outbox.js';
import { useOutbox } from '../../lib/offline/useOutbox.js';
import { LessonPdfButton } from '../../components/lesson/LessonPdfButton.js';
import { localName, t, formatDateTime } from '../../lib/i18n.js';

/**
 * The field capture form (spec §10).
 *
 * Guided and dropdown-first, because it is filled in on a phone, on site, often
 * one-handed and often in the sun. Three things follow from that and none of them
 * is cosmetic:
 *
 *  1. **Nothing here asks the user to type an identifier.** Project, discipline
 *     and categorisation are dropdowns fed by master data (Rule 1) — the same
 *     lists an administrator maintains, so adding a discipline never needs a
 *     deploy.
 *  2. **Date/time and GPS are captured, not entered.** Spec §10 requires it, and
 *     it is an evidence requirement: a coordinate somebody typed proves nothing
 *     about where they stood. A failed fix records WHY it failed and the form
 *     still submits, because a lesson is worth more than its coordinates.
 *  3. **Offline is the normal path, not the fallback.** A form created with no
 *     signal is written to the outbox with the id the server will use, so the
 *     photos queued beside it already know which record they belong to.
 */

const ISSUE_CATEGORIES: { value: IssueCategory; label: string; hint: string }[] = [
  { value: 'minor', label: 'Minor', hint: 'No rework, no schedule impact' },
  { value: 'moderate', label: 'Moderate', hint: 'Localised rework or delay' },
  { value: 'major', label: 'Major', hint: 'Significant rework, cost or schedule impact' },
];

const IMPACTS: { value: IssueImpact; label: string; hint: string }[] = [
  { value: 'negative', label: 'Negative', hint: 'Something went wrong' },
  { value: 'positive', label: 'Positive', hint: 'Something worth repeating' },
];

const NARRATIVE_FIELDS: RephrasableField[] = [
  'description',
  'rootCause',
  'correction',
  'correctiveAction',
];

interface Draft {
  projectId: string;
  title: string;
  disciplineId: string;
  categorisationId: string;
  issueCategory: IssueCategory;
  impact: IssueImpact;
  description: string;
  rootCause: string;
  correction: string;
  correctiveAction: string;
  isRepeatedIssue: boolean;
  repeatCount: string;
  repeatLocation: string;
}

const EMPTY_DRAFT: Draft = {
  projectId: '',
  title: '',
  disciplineId: '',
  categorisationId: '',
  issueCategory: 'moderate',
  impact: 'negative',
  description: '',
  rootCause: '',
  correction: '',
  correctiveAction: '',
  isRepeatedIssue: false,
  repeatCount: '',
  repeatLocation: '',
};

function draftFromLesson(lesson: Lesson): Draft {
  return {
    projectId: lesson.projectId,
    title: lesson.title,
    disciplineId: lesson.disciplineId ?? '',
    categorisationId: lesson.categorisationId ?? '',
    issueCategory: lesson.issueCategory,
    impact: lesson.impact,
    description: lesson.description,
    rootCause: lesson.rootCause,
    correction: lesson.correction,
    correctiveAction: lesson.correctiveAction,
    isRepeatedIssue: lesson.isRepeatedIssue,
    repeatCount: lesson.repeatCount === null ? '' : String(lesson.repeatCount),
    repeatLocation: lesson.repeatLocation ?? '',
  };
}

export function LessonForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const outbox = useOutbox();

  const isNew = !id || id === 'new';

  const [lessonId, setLessonId] = useState(() => (isNew ? crypto.randomUUID() : id));
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  /** What the server last confirmed — the `base` for the three-way merge. */
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<Lesson | null>(null);
  const [photos, setPhotos] = useState<LessonPhoto[]>([]);
  const [gps, setGps] = useState<GpsFix | null>(null);
  const [capturedAt] = useState(() => new Date().toISOString());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * An AI draft applied from a sentence (spec §13.3), until the lesson is saved or
   * the draft is discarded. Only its id and the answers given are kept: what the
   * person did with it is judged by the server from the saved lesson.
   */
  const [promptDraft, setPromptDraft] = useState<{ suggestionId: string; answers: Record<string, string> } | null>(null);

  // Master data for the dropdowns. Cached long: these change rarely, and a field
  // user opening the form on a weak signal should not wait on them.
  const reference = useQuery({
    queryKey: ['reference', 'lesson-learned'],
    queryFn: () => fetchReference('lesson-learned'),
    staleTime: 10 * 60_000,
  });

  const existing = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => fetchLesson(lessonId),
    enabled: !isNew,
    retry: false,
  });

  // Capture the location as soon as a new form opens, so the fix has arrived by
  // the time the user reaches the bottom of the page.
  useEffect(() => {
    if (!isNew) return;
    void captureGps().then(setGps);
  }, [isNew]);

  useEffect(() => {
    if (!existing.data) return;
    const next = draftFromLesson(existing.data.lesson);
    setDraft(next);
    setBaseline(next);
    setSaved(existing.data.lesson);
    setPhotos(existing.data.photos);
  }, [existing.data]);

  // Offline and opening a record that was never online: fall back to the mirror.
  useEffect(() => {
    if (isNew || !existing.isError) return;
    void readCachedLesson(lessonId).then((cached) => {
      if (!cached) return;
      const next = draftFromLesson(cached as unknown as Lesson);
      setDraft(next);
      setBaseline(next);
      setNotice(t("Showing the copy held on this device. It has not synced yet."));
    });
  }, [isNew, existing.isError, lessonId]);

  const readOnly = saved !== null && saved.status !== 'draft' && saved.status !== 'sent_back';

  const projects = reference.data?.projects ?? [];
  const disciplines = reference.data?.disciplines ?? [];
  const categorisations = reference.data?.categorisations ?? [];

  const canSave = useMemo(
    () => draft.projectId !== '' && draft.title.trim() !== '',
    [draft.projectId, draft.title],
  );

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  /** The body both the online create and the queued create send. */
  const createBody = () => ({
    id: lessonId,
    projectId: draft.projectId,
    title: draft.title.trim(),
    disciplineId: draft.disciplineId || null,
    categorisationId: draft.categorisationId || null,
    issueCategory: draft.issueCategory,
    impact: draft.impact,
    capturedAt,
    gpsCaptureStatus: gps?.status ?? 'unavailable',
    gpsLatitude: gps?.latitude ?? null,
    gpsLongitude: gps?.longitude ?? null,
    gpsAccuracyMetres: gps?.accuracyMetres ?? null,
    description: draft.description,
    rootCause: draft.rootCause,
    correction: draft.correction,
    correctiveAction: draft.correctiveAction,
    isRepeatedIssue: draft.isRepeatedIssue,
    repeatCount: draft.repeatCount ? Number(draft.repeatCount) : null,
    repeatLocation: draft.repeatLocation || null,
  });

  const updateBody = () => ({
    title: draft.title.trim(),
    disciplineId: draft.disciplineId || null,
    categorisationId: draft.categorisationId || null,
    issueCategory: draft.issueCategory,
    impact: draft.impact,
    description: draft.description,
    rootCause: draft.rootCause,
    correction: draft.correction,
    correctiveAction: draft.correctiveAction,
    isRepeatedIssue: draft.isRepeatedIssue,
    repeatCount: draft.repeatCount ? Number(draft.repeatCount) : null,
    repeatLocation: draft.repeatLocation || null,
  });

  /**
   * Queue the work rather than send it.
   *
   * Field edits carry the value the device started from, which is what makes the
   * server's three-way merge possible. Sending the whole record instead would
   * force the server to guess whose version is newer, and guessing is how
   * somebody's work disappears — which spec §10 forbids outright.
   */
  const queueOffline = async () => {
    if (!saved) {
      await queueCreate(lessonId, createBody());
    } else {
      // Every field a person can change, as text (DATA-3). Only six used to be
      // queued, and the rest were dropped while the page said "saved".
      const asText = (value: Draft) => ({
        title: value.title,
        description: value.description,
        rootCause: value.rootCause,
        correction: value.correction,
        correctiveAction: value.correctiveAction,
        repeatLocation: value.repeatLocation,
        disciplineId: value.disciplineId,
        categorisationId: value.categorisationId,
        issueCategory: value.issueCategory,
        impact: value.impact,
        isRepeatedIssue: value.isRepeatedIssue ? 'true' : 'false',
        repeatCount: value.repeatCount,
      });
      const now = asText(draft);
      const was = baseline ? asText(baseline) : null;
      for (const field of Object.keys(now) as (keyof typeof now)[]) {
        const value = now[field] ?? '';
        const base = was?.[field] ?? '';
        if (value !== base) await queueFieldChange(lessonId, field, base, value);
      }
    }
    await outbox.refresh();
  };

  /**
   * A photo is queued first, always — then sent at once when there is a signal
   * (DATA-5). It used to wait for the next reconnect or app start.
   */
  const photoQueued = () => {
    void outbox.refresh();
    if (outbox.online) {
      void outbox.flushNow().then(() => queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] }));
    }
  };

  /** Whether the form holds anything the server has not confirmed. */
  const dirty = !saved || JSON.stringify(draft) !== JSON.stringify(baseline);

  /**
   * Save online. A lesson this device created offline may already have synced:
   * the create then reports `existing`, and the edits made since go in as an
   * update — they used to be dropped (DATA-6).
   */
  const persist = async (): Promise<Lesson> => {
    if (saved) return (await updateLesson(lessonId, updateBody())).lesson;
    const created = await createLesson(createBody());
    if (created.existing) return (await updateLesson(lessonId, updateBody())).lesson;
    return created.lesson;
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!outbox.online) {
        await queueOffline();
        return null;
      }
      return persist();
    },
    onSuccess: (lesson) => {
      setError(null);
      if (!lesson) {
        setNotice(t("Saved on this device. It will sync when you have a signal."));
        return;
      }
      if (promptDraft) {
        // Best effort: the lesson is saved either way, and the suggestion simply
        // stays "pending" in the AI activity log if this does not arrive.
        void disposePromptDraft(promptDraft.suggestionId, 'accepted', promptDraft.answers).catch(() => undefined);
        setPromptDraft(null);
      }
      setSaved(lesson);
      setBaseline(draftFromLesson(lesson));
      setLessonId(lesson.id);
      setNotice(t("Saved as {0}.", { 0: lesson.referenceNo }));
      void queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
    onError: async (caught) => {
      // A failed save is never a lost form. It goes to the outbox, where the
      // next flush will try again and the user can see it waiting.
      await queueOffline();
      setError(
        caught instanceof ApiError
          ? t("{0} Your entry is saved on this device and will retry.", { 0: caught.message })
          : t("Saved on this device — the server could not be reached."),
      );
    },
  });

  /**
   * Turn a refusal into something actionable.
   *
   * The API returns WHICH fields are missing in the error's details, and showing
   * only "this cannot be submitted yet" throws that away — leaving a field
   * engineer to guess which of four narrative boxes the server meant. A refusal
   * a user cannot act on is barely better than a crash.
   */
  const describeSubmitError = (caught: unknown): string => {
    if (!(caught instanceof ApiError)) {
      return caught instanceof Error && caught.message
        ? caught.message
        : 'Submission needs a connection. Your entry is saved on this device.';
    }
    const details = caught.details as { blockers?: string[] } | undefined;
    const blockers = details?.blockers;
    return blockers?.length ? `${caught.message} ${blockers.join(' ')}` : caught.message;
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (!outbox.online) {
        throw new Error(t("Submission needs a connection. Your entry is saved on this device."));
      }
      // Submit what is on the screen (DATA-4): unsaved edits used to be left
      // behind, and the lesson went for approval without them.
      if (dirty) {
        const lesson = await persist();
        setSaved(lesson);
        setBaseline(draftFromLesson(lesson));
      }
      // Photos first (DATA-5): a photo still waiting on this device would be
      // refused once the lesson is submitted, and reported as "Rejected".
      await outbox.flushNow();
      const waiting = (await listQueuedPhotos(lessonId)).length;
      if (waiting > 0) {
        throw new Error(
          t("{0} photo{1} still waiting to upload from this device. Submit once {2} gone — nothing has been lost.", { 0: waiting, 1: waiting === 1 ? ' is' : 's are', 2: waiting === 1 ? 'it has' : 'they have' }),
        );
      }
      return submitLesson(lessonId);
    },
    onSuccess: (result) => {
      setSaved(result.lesson);
      void queryClient.invalidateQueries({ queryKey: ['lessons'] });
      navigate('/lesson-learned/log');
    },
    onError: (caught) => setError(describeSubmitError(caught)),
  });

  return (
    <div className="min-h-full">
      <AppTopBar app="lesson-learned" />
      <OfflineBanner state={outbox} />
      <Breadcrumbs app="lesson-learned" trail={[saved?.referenceNo ?? 'New lesson']} />

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">
          {saved ? saved.referenceNo : t("New Lesson Learned")}
        </h1>
        <p className="mt-1 text-sm text-content-secondary">
          {saved
            ? t("Status: {0}", { 0: saved.status.replace('_', ' ') })
            : t("The reference number is issued when this first reaches the server.")}
        </p>

        {saved?.status === 'sent_back' && saved.sentBackRemarks && (
          <div
            role="alert"
            className="mt-4 rounded-md p-3 text-sm"
            style={{
              background: 'var(--tone-rejected-surface)',
              borderInlineStartWidth: '4px',
              borderInlineStartColor: 'var(--tone-rejected)',
            }}
          >
            <strong>{t("Sent back for revision.")}</strong> {saved.sentBackRemarks}
          </div>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}
        {notice && !error && (
          <p role="status" className="mt-4 text-sm text-content-secondary">
            {notice}
          </p>
        )}

        {!saved && isNew && (
          <PromptDraftPanel
            lessonId={lessonId}
            online={outbox.online}
            onApply={(result) => {
              setDraft((current) => ({ ...current, ...result.fields }) as Draft);
              setPromptDraft({ suggestionId: result.suggestionId!, answers: {} });
            }}
            onAnswer={(field, value) => {
              setDraft((current) => ({ ...current, [field]: value }) as Draft);
              setPromptDraft((current) =>
                current ? { ...current, answers: { ...current.answers, [field]: value } } : current,
              );
            }}
            onDiscard={() => {
              if (promptDraft) void disposePromptDraft(promptDraft.suggestionId, 'rejected').catch(() => undefined);
              setPromptDraft(null);
              setDraft(EMPTY_DRAFT);
            }}
          />
        )}

        {/* --- What and where ------------------------------------------- */}
        <section className="mt-6 rounded-md border border-border bg-surface p-5">
          <h2 className="m-0 text-base font-semibold">{t("What and where")}</h2>

          <div className="mt-4 grid gap-4">
            <div>
              <label htmlFor="project" className="text-sm font-medium">
                {t("Project")}
              </label>
              <select
                id="project"
                value={draft.projectId}
                disabled={saved !== null || readOnly}
                onChange={(event) => set('projectId', event.target.value)}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm disabled:bg-surface-sunken"
              >
                <option value="">{t("Choose a project…")}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {localName(project)}
                  </option>
                ))}
              </select>
              {reference.isError && (
                <p className="mt-1 text-xs text-content-secondary">
                  {t("The project list could not be loaded. Reconnect to start a new lesson.")}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="title" className="text-sm font-medium">
                {t("Title")}
              </label>
              <input
                id="title"
                value={draft.title}
                readOnly={readOnly}
                onChange={(event) => set('title', event.target.value)}
                placeholder={t("A short name a colleague would search for")}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm read-only:bg-surface-sunken"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="discipline" className="text-sm font-medium">
                  {t("Discipline")}
                </label>
                <select
                  id="discipline"
                  value={draft.disciplineId}
                  disabled={readOnly}
                  onChange={(event) => set('disciplineId', event.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm disabled:bg-surface-sunken"
                >
                  <option value="">{t("Not specified")}</option>
                  {disciplines.map((discipline) => (
                    <option key={discipline.id} value={discipline.id}>
                      {localName(discipline)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="categorisation" className="text-sm font-medium">
                  {t("Categorisation")}
                </label>
                <select
                  id="categorisation"
                  value={draft.categorisationId}
                  disabled={readOnly}
                  onChange={(event) => set('categorisationId', event.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm disabled:bg-surface-sunken"
                >
                  <option value="">{t("Not specified")}</option>
                  {categorisations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.category} — {t(item.label)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <fieldset className="m-0 border-0 p-0">
              <legend className="text-sm font-medium">{t("Severity")}</legend>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {ISSUE_CATEGORIES.map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer rounded-sm border px-3 py-2 text-sm ${
                      draft.issueCategory === option.value
                        ? 'border-ag-purple bg-surface-sunken font-medium'
                        : 'border-border-strong'
                    }`}
                  >
                    <input
                      type="radio"
                      name="issueCategory"
                      value={option.value}
                      checked={draft.issueCategory === option.value}
                      disabled={readOnly}
                      onChange={() => set('issueCategory', option.value)}
                      className="sr-only"
                    />
                    {t(option.label)}
                    <span className="block text-xs font-normal text-content-secondary">
                      {option.hint}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="m-0 border-0 p-0">
              <legend className="text-sm font-medium">{t("Impact")}</legend>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {IMPACTS.map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer rounded-sm border px-3 py-2 text-sm ${
                      draft.impact === option.value
                        ? 'border-ag-purple bg-surface-sunken font-medium'
                        : 'border-border-strong'
                    }`}
                  >
                    <input
                      type="radio"
                      name="impact"
                      value={option.value}
                      checked={draft.impact === option.value}
                      disabled={readOnly}
                      onChange={() => set('impact', option.value)}
                      className="sr-only"
                    />
                    {t(option.label)}
                    <span className="block text-xs font-normal text-content-secondary">
                      {option.hint}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Captured, never typed (spec §10). */}
          <dl className="mt-5 grid gap-x-6 gap-y-2 border-t border-border pt-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-content-secondary">
                {t("Captured at")}
              </dt>
              <dd className="m-0">{formatDateTime(saved?.capturedAt ?? capturedAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-content-secondary">{t("Location")}</dt>
              <dd className="m-0">
                {saved
                  ? describeGps(saved)
                  : gps === null
                    ? t("Locating…")
                    : gps.status === 'captured'
                      ? `${gps.latitude?.toFixed(5)}, ${gps.longitude?.toFixed(5)}${
                          gps.accuracyMetres ? ` (±${Math.round(gps.accuracyMetres)} m)` : ''
                        }`
                      : describeGps({
                          gpsCaptureStatus: gps.status,
                          gpsLatitude: null,
                          gpsLongitude: null,
                        })}
              </dd>
            </div>
          </dl>
          <p className="mt-2 text-xs text-content-secondary">
            {t("Both are recorded by the device. Neither can be typed, and a location that could not be obtained is recorded as such rather than left blank.")}
          </p>
        </section>

        {/* --- The narrative -------------------------------------------- */}
        <section className="mt-6 grid gap-4">
          <h2 className="m-0 text-base font-semibold">{t("The lesson")}</h2>

          {NARRATIVE_FIELDS.map((field) =>
            saved ? (
              <RephrasePanel
                key={field}
                lessonId={lessonId}
                field={field}
                value={draft[field]}
                readOnly={readOnly}
                online={outbox.online}
                onChange={(value) => set(field, value)}
                onSaved={(value) =>
                  setBaseline((current) => (current ? { ...current, [field]: value } : current))
                }
              />
            ) : (
              <section key={field} className="rounded-md border border-border bg-surface p-4">
                <label htmlFor={`field-${field}`} className="text-sm font-semibold">
                  {field === 'description'
                    ? t("Description of the issue")
                    : field === 'rootCause'
                      ? t("Root cause")
                      : field === 'correction'
                        ? t("Correction taken")
                        : t("Corrective action")}
                </label>
                <p className="mt-1 text-xs text-content-secondary">
                  {t("AI rephrasing becomes available once this lesson is saved.")}
                </p>
                <textarea
                  id={`field-${field}`}
                  value={draft[field]}
                  onChange={(event) => set(field, event.target.value)}
                  rows={4}
                  className="mt-2 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
                />
              </section>
            ),
          )}
        </section>

        {/* --- Repetition ----------------------------------------------- */}
        <section className="mt-6 rounded-md border border-border bg-surface p-5">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={draft.isRepeatedIssue}
              disabled={readOnly}
              onChange={(event) => set('isRepeatedIssue', event.target.checked)}
            />
            {t("This has happened before")}
          </label>

          {draft.isRepeatedIssue && (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="repeat-count" className="text-sm font-medium">
                  {t("How many times")}
                </label>
                <input
                  id="repeat-count"
                  type="number"
                  min={1}
                  value={draft.repeatCount}
                  readOnly={readOnly}
                  onChange={(event) => set('repeatCount', event.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm read-only:bg-surface-sunken"
                />
              </div>
              <div>
                <label htmlFor="repeat-location" className="text-sm font-medium">
                  {t("Where")}
                </label>
                <input
                  id="repeat-location"
                  value={draft.repeatLocation}
                  readOnly={readOnly}
                  onChange={(event) => set('repeatLocation', event.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm read-only:bg-surface-sunken"
                />
              </div>
            </div>
          )}
        </section>

        {/* --- Photographs ---------------------------------------------- */}
        <section className="mt-6 grid gap-4">
          <h2 className="m-0 text-base font-semibold">{t("Photo evidence")}</h2>
          {saved ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <PhotoCapture
                lessonId={lessonId}
                category="before"
                uploaded={photos.filter((photo) => photo.category === 'before')}
                readOnly={readOnly}
                onQueued={photoQueued}
              />
              <PhotoCapture
                lessonId={lessonId}
                category="after"
                uploaded={photos.filter((photo) => photo.category === 'after')}
                readOnly={readOnly}
                onQueued={photoQueued}
              />
            </div>
          ) : (
            <p className="text-sm text-content-secondary">
              {t("Save the lesson first — a photo has to know which record it belongs to.")}
            </p>
          )}
        </section>

        {saved && <LessonPdfButton lessonId={saved.id} referenceNo={saved.referenceNo} online={outbox.online} />}

        {/* --- Actions --------------------------------------------------- */}
        {!readOnly && (
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!canSave || save.isPending}
              className="rounded-sm border border-border-strong px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {save.isPending ? t("Saving…") : outbox.online ? t("Save draft") : t("Save on this device")}
            </button>

            <button
              type="button"
              onClick={() => submit.mutate()}
              disabled={!saved || submit.isPending || !outbox.online}
              title={
                !saved
                  ? t("Save the lesson first.")
                  : !outbox.online
                    ? t("Submitting needs a connection. Your entry is safe on this device.")
                    : undefined
              }
              className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-50"
            >
              {submit.isPending ? t("Submitting…") : t("Submit for approval")}
            </button>
          </div>
        )}

        <p className="mt-3 text-xs text-content-secondary">
          {t("Your name, designation and signature are attached automatically at submission — they are taken from your user record, never typed here.")}
        </p>
      </main>
    </div>
  );
}
